import { Box, CircularProgress } from "@mui/material";
import {
  DataGridPremium,
  DataGridPremiumProps,
  useGridApiRef,
  GridInitialState,
  GRID_AGGREGATION_FUNCTIONS,
} from "@mui/x-data-grid-premium";
import React, { useRef, useState, useCallback, useEffect } from "react";
import GridCustomToolbar from "./CustomToolbar";
import { minDateAggregation, maxDateAggregation } from "utils/dataGrid";

export interface GaneshDataGridProps extends DataGridPremiumProps {
  gridId: string;
  loading?: boolean;
  data?: any[];
  defaultGridState?: GridInitialState;
  customHeight?: string;
  customWidth?: string;
  showGridSaver?: boolean;
  headerFilters?: boolean;
  compactMode?: boolean; // New prop for compact height behavior
  getDetailPanelContent?: (params: any) => React.ReactNode;
  getDetailPanelHeight?: () => number;
  detailPanelExpandedRowIds?: string[];
}

const GaneshDataGrid: React.FC<GaneshDataGridProps> = ({
  gridId,
  loading,
  data,
  defaultGridState,
  customHeight,
  customWidth,
  showGridSaver,
  headerFilters = true,
  compactMode = false,
  ...props
}) => {
  const apiRef = useGridApiRef();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, scrollLeft: 0 });

  if (loading) {
    data = [];
  }

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleMouseDown = (e: MouseEvent) => {
      // Only activate on right mouse button (button 2)
      if (e.button !== 2) return;

      e.preventDefault();

      // Find the DataGrid virtualization container
      const virtualScroller = container.querySelector(
        ".MuiDataGrid-virtualScroller"
      ) as HTMLElement;
      if (!virtualScroller) return;

      setIsDragging(true);
      setDragStart({
        x: e.pageX,
        scrollLeft: virtualScroller.scrollLeft,
      });
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;

      e.preventDefault();
      const virtualScroller = container.querySelector(
        ".MuiDataGrid-virtualScroller"
      ) as HTMLElement;
      if (!virtualScroller) return;

      const x = e.pageX;
      const walk = (x - dragStart.x) * 1.5; // Sensitivity
      virtualScroller.scrollLeft = dragStart.scrollLeft - walk;
    };

    const handleMouseUp = (e: MouseEvent) => {
      // Only stop dragging on right mouse button release
      if (e.button === 2) {
        setIsDragging(false);
      }
    };

    const handleContextMenu = (e: MouseEvent) => {
      // Always prevent context menu
      e.preventDefault();
    };

    const handleMouseLeave = () => {
      setIsDragging(false);
    };

    // Add event listeners
    container.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    container.addEventListener("contextmenu", handleContextMenu);
    container.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      container.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      container.removeEventListener("contextmenu", handleContextMenu);
      container.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, [isDragging, dragStart]);

  const CustomToolbarWithGridId = () => (
    <GridCustomToolbar
      gridId={gridId}
      apiRef={apiRef}
      defaultGridState={defaultGridState}
      showGridSaver={showGridSaver}
    />
  );

  return (
    <Box
      ref={containerRef}
      sx={{
        height: compactMode ? "auto" : customHeight || "90vh",
        width: customWidth || "100%",
        paddingTop: compactMode ? "0px" : "20px",
        cursor: isDragging ? "grabbing" : "default",
        userSelect: isDragging ? "none" : "auto",
      }}
    >
      <DataGridPremium
        {...props}
        initialState={props.initialState || defaultGridState}
        aggregationFunctions={{
          ...GRID_AGGREGATION_FUNCTIONS,
          minDate: minDateAggregation,
          maxDate: maxDateAggregation,
        }}
        slots={{
          loadingOverlay: () => (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="100%"
            >
              <CircularProgress />
            </Box>
          ),
          toolbar: CustomToolbarWithGridId,
        }}
        rows={data}
        columns={props.columns}
        loading={loading}
        apiRef={apiRef}
        pagination={!compactMode}
        headerFilters={headerFilters}
        pageSizeOptions={[10, 25, 50, 100, 250, 500, 1000]}
        localeText={{
          MuiTablePagination: {
            labelDisplayedRows: ({ from, to, count }) =>
              `${from.toLocaleString()}–${to.toLocaleString()} of ${count.toLocaleString()}`,
          },
        }}
        sx={{
          ...(compactMode && {
            "& .MuiDataGrid-root": { height: "auto !important" },
            "& .MuiDataGrid-main": { height: "auto !important" },
            "& .MuiDataGrid-virtualScroller": { height: "auto !important" },
            "& .MuiDataGrid-virtualScrollerContent": {
              height: "auto !important",
            },
            "& .MuiDataGrid-virtualScrollerRenderZone": {
              height: "auto !important",
            },
          }),
          "& .MuiDataGrid-columnHeaderTitle": {
            whiteSpace: "normal",
            lineHeight: "normal",
            fontWeight: "bold",
          },
          "& .MuiDataGrid-columnHeader": {
            height: "auto !important",
            minHeight: "60px !important",
          },
          "& .MuiDataGrid-columnHeaders": {
            maxHeight: "190px !important",
          },
          "& .MuiDataGrid-row": {
            "&:nth-of-type(odd)": {
              backgroundColor: "#f9f9f9",
            },
            "&:nth-of-type(even)": {
              backgroundColor: "#fff",
            },
          },
          "& .MuiDataGrid-row.Mui-selected": {
            backgroundColor: "rgba(25, 118, 210, 0.38) !important",
          },
          "& .MuiDataGrid-cell": {
            "&:focus": {
              outline: "none",
            },
            "&.Mui-selected": {
              outline: "none",
            },
          },
          "& .MuiDataGrid-bottomContainer .MuiDataGrid-row.MuiDataGrid-row--lastVisible":
            {
              color: "#1565c0",
              fontWeight: 500,
              fontSize: "1.2em",
            },
          "& .MuiDataGrid-overlayWrapper": {
            zIndex: -1,
          },
          ...props.sx,
        }}
      />
    </Box>
  );
};

export default GaneshDataGrid;
